import {
  FC, FormEvent, useEffect, useState, useRef, TouchEvent, MouseEvent,
} from 'react';
import classNames from 'classnames';
import { FEATURE_FLAGS_COOKIE_NAME } from 'src/common/constants';
import { ENABLED_FEATURES, FEATURES } from 'src/common/hooks/useFeatureFlag';
import useOpenCloseToggle from 'src/common/hooks/useOpenCloseToggle';
import { useRequestInfoContext } from 'src/common/providers/RequestInfoProvider';
import isDarkCode from 'src/common/utils/isDarkCode';
import getClassNames from 'src/common/utils/getClassNames';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import Checkbox from '../Checkbox';
import Modal from '../ModalDS22';
import Button from '../ButtonDS22';
import AbTestVariantSelector from '../AbTestVariantSelector';

import styles from './FeatureFlagModal.module.scss';

// How long to wait before opening the modal (in ms)
const LONG_PRESS_TIME = 800;

export type FeatureFlags = {
  showSelector: () => void;
};

const removeBlankString = (s: string) => s !== '';

const featuresHaveUpdated = (a: string[], b: string[]) => (
  !a.every((x) => b.includes(x)) || !b.every((x) => a.includes(x))
);

const FeatureFlagModal: FC = () => {
  const { close, isOpen, open } = useOpenCloseToggle();
  const { featureFlags } = useRequestInfoContext();
  const [updatedFeatureFlags, setUpdatedFeatureFlags] = useState(featureFlags);
  const buttonPressTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isPressed, setIsPressed] = useState(false);

  const toggleFeatureFlag = (feature: string) => {
    setUpdatedFeatureFlags((features) => (
      features.includes(feature) ? features.filter((x) => x !== feature) : [...features, feature]
    ));
  };

  const closeModal = () => {
    // Reset the modal state to match cookie
    setUpdatedFeatureFlags(featureFlags);
    close();
  };

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (featuresHaveUpdated(featureFlags, updatedFeatureFlags)) {
      document.cookie = `${FEATURE_FLAGS_COOKIE_NAME}=${updatedFeatureFlags.filter(removeBlankString).join()}; path=/`;
    }
    // Reload to ensure all settings are applied
    // eslint-disable-next-line no-restricted-globals
    location.reload();
  };

  const handleButtonPress = (e: MouseEvent<HTMLButtonElement> | TouchEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsPressed(true);
    // Start the long press timer
    buttonPressTimeoutIdRef.current = setTimeout(() => {
      open();
    }, LONG_PRESS_TIME);
  };

  const clearButtonPress = () => {
    setIsPressed(false);
    if (buttonPressTimeoutIdRef.current) {
      clearTimeout(buttonPressTimeoutIdRef.current);
      buttonPressTimeoutIdRef.current = null;
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.gmb = window.gmb || {};
      window.gmb.featureFlags = {
        showSelector: open,
      };
    }
  }, [open]);

  // Make sure we get rid of the timeout when the component unmounts
  useEffect(() => () => {
    const timeoutId = buttonPressTimeoutIdRef.current;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }, []);

  return (
    <>
      <Modal
        label="Feature Selector"
        modalOpen={isOpen}
        closeModal={close}
        position="middle"
        size="large"
      >
        <Modal.ControllerDS22
          onClose={close}
        />

        <Modal.HeaderDS22
          description="Select which features you&apos;d like to have enabled"
          descriptionsSize="body-m"
          title="Select Features"
          titleSize="heading-4"
        />

        <Modal.BodyDS22
          slot1={(
            <div className={styles.body}>
              {!!ENABLED_FEATURES.length && (
                <>
                  <div>
                    <Typography type="h2" size="heading-6" weight="semibold" className={styles.text}>Enabled for all users</Typography>
                    <Typography type="p" size="body-m" className={styles.text}>
                      These features are released but can be rolled back.
                    </Typography>
                  </div>
                  <ul className={styles.list}>
                    {ENABLED_FEATURES.map((feature) => (
                      <li
                        key={feature}
                        className={styles.item}
                      >
                        <label className={styles.enabled}>
                          <Icon id="star" />
                          {feature}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <Typography type="h2" size="heading-6" weight="semibold" className={styles.text}>Optional</Typography>
                </>
              )}
              <form
                method="POST"
                id="features"
                onSubmit={onFormSubmit}
              >
                <ul className={styles.list}>
                  {FEATURES
                    .filter((feature) => !ENABLED_FEATURES.includes(feature))
                    .map((feature) => (
                      <li
                        key={feature}
                        className={styles.item}
                      >
                        <label className={styles.label}>
                          <Checkbox
                            checked={updatedFeatureFlags.includes(feature)}
                            onChange={() => toggleFeatureFlag(feature)}
                          />
                          {feature}
                        </label>
                      </li>
                    ))}

                </ul>
                <AbTestVariantSelector />
              </form>
            </div>
          )}
          spacingBottom="m"
        />
        <Modal.FooterDS22
          buttonPrimary={(
            <Button formId="features" type="submit">Save</Button>
          )}
          buttonSecondary={(
            <Button variant="tertiary" onClick={closeModal}>Cancel</Button>
          )}
        />
      </Modal>
      {isDarkCode() ? (
        <button
          type="button"
          className={getClassNames(styles, 'hiddenButton', classNames({ pressed: isPressed }))}
          aria-label="Open Feature Selector"
          onMouseDown={handleButtonPress}
          onMouseUp={clearButtonPress}
          onMouseLeave={clearButtonPress}
          onTouchStart={handleButtonPress}
          onTouchEnd={clearButtonPress}
          onTouchCancel={clearButtonPress}
          tabIndex={-1}
        />
      ) : null}
    </>
  );
};

export default FeatureFlagModal;
